import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import {
  FormControl,
  Button,
  FormHelperText,
  Grid,
  Typography,
  ListItem,
  Divider,
} from "@material-ui/core";
import { InlineDatePicker } from "material-ui-pickers";
import InfoOutlined from "@material-ui/icons/InfoOutlined";

import { idsFarois } from "constants/idsFarois";
import * as farolActions from "features/manejo/redux/actions/farolActions";
import { diferencaEntreDatas } from "lib/tratamentoDatas";
import { enviaDadosGA } from "shared/utils.js";
import { useTranslations } from "adapters/i18n";

type DispatchProps = typeof mapDispatchToProps;

type PeriodoColeta = {
  inicio: Date | null;
  final: Date | null;
};

const initialState = {
  desabilitaBotao: false,
  periodoColeta: null,
  ultimoAlterado: "",
  erros: {
    inicio: "",
    final: "",
  },
};
let periodoInicial: PeriodoColeta | null = null;

interface FiltroPeriodoColetaProps extends DispatchProps {
  periodoColeta: PeriodoColeta;
  farolSelecionado: number | null;
}

const FiltroPeriodoColeta = ({
  periodoColeta,
  farolSelecionado,
  resetPeriodoColeta,
  alteraPeriodoColeta,
  listaFarol,
}: FiltroPeriodoColetaProps) => {
  periodoInicial = periodoColeta;
  const { t } = useTranslations();
  const [desabilitaBotao, setDesabilitaBotao] = useState(
    initialState.desabilitaBotao
  );
  const [innerPeriodoColeta, setInnerPeriodoColeta] =
    useState<PeriodoColeta>(periodoColeta);
  const [erros, setErros] = useState(initialState.erros);

  const handlePeriodoColeta = (event: Date, name: "inicio" | "final") => {
    if (event === innerPeriodoColeta[name]) return;

    const changePeriodo = { ...innerPeriodoColeta, [name]: event };
    const diferencaDatas = diferencaEntreDatas(
      changePeriodo.inicio,
      changePeriodo.final,
      "dias"
    );

    const copiaErros = { ...erros };

    if (diferencaDatas > 90) {
      copiaErros[name] = t("statusIndicator:dates-diff-message", { value: 90 });
      setErros(copiaErros);
      setDesabilitaBotao(true);
    } else if (diferencaDatas < 0) {
      copiaErros[name] = t("statusIndicator:start-date-greater-end-date");
      setErros(copiaErros);
      setDesabilitaBotao(true);
    } else {
      setErros({ ...initialState.erros });
      setDesabilitaBotao(false);
    }

    setInnerPeriodoColeta(changePeriodo);
  };

  const resetPeriodo = () => {
    setInnerPeriodoColeta({ inicio: null, final: null });
    resetPeriodoColeta();
    listaFarol(false);
  };

  const aplicaFiltroPeriodo = () => {
    alteraPeriodoColeta(innerPeriodoColeta);
    listaFarol(false);
    const diasFiltrados = diferencaEntreDatas(
      innerPeriodoColeta.inicio,
      innerPeriodoColeta.final,
      "dias"
    );
    const acao = `Filtrar periodo coleta em ${diasFiltrados} dias`;
    const valor = diasFiltrados;
    const categoria = "Análise";
    const rotulo = "Coleta";
    enviaDadosGA(acao, rotulo, categoria, valor);
  };

  const renderBotoes = () => {
    return (
      <Grid container spacing={16} justify={"flex-end"} alignItems={"flex-end"}>
        <Grid item>
          <Button
            onClick={resetPeriodo}
            color="primary"
            style={{ margin: 0, borderRadius: "18px" }}
          >
            {t("common:actions.clear-date")}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={aplicaFiltroPeriodo}
            disabled={desabilitaBotao}
            color="primary"
            style={{ margin: 0, borderRadius: "18px" }}
          >
            {t("common:actions.apply")}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <ListItem key="FiltroPeriodoListItem">
        <Grid container spacing={16}>
          <Grid item xs={6}>
            <Typography align="left" variant="subtitle1" gutterBottom>
              {t("common:period")}:
            </Typography>
          </Grid>
          {farolSelecionado === idsFarois.alturaPastoTaxaCrescimento ||
          farolSelecionado === idsFarois.alturaPastoMediaSaida ||
          farolSelecionado === idsFarois.alturaPastoMediaEntrada ? (
            <div />
          ) : (
            <Grid
              item
              xs={12}
              container
              justify="flex-start"
              alignItems="center"
              spacing={8}
            >
              <Grid item>
                <InfoOutlined color="primary" />
              </Grid>
              <Grid item>
                {" "}
                <Typography variant="body2" color="textSecondary">
                  {t("statusIndicator:last-collect-info")}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            container
            justify="space-between"
            alignItems="flex-start"
            spacing={8}
          >
            <Grid item xs={6}>
              <FormControl style={{ maxWidth: "100%" }}>
                <InlineDatePicker
                  onlyCalendar
                  keyboard
                  label={t("statusIndicator:beginning")}
                  disableFuture
                  invalidDateMessage={t("common:validation.invalid-date-format")}
                  maxDateMessage={t("common:validation.max-date")}
                  minDateMessage={t("common:validation.min-date")}
                  format="DD/MM/YYYY"
                  mask={(value) =>
                    value
                      ? [
                          /\d/,
                          /\d/,
                          "/",
                          /\d/,
                          /\d/,
                          "/",
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/,
                        ]
                      : []
                  }
                  disableOpenOnEnter
                  animateYearScrolling={false}
                  value={innerPeriodoColeta.inicio}
                  onChange={(e) => handlePeriodoColeta(e, "inicio")}
                />
                {erros.inicio !== "" && (
                  <FormHelperText error id="inicio-erro">
                    {erros.inicio}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl style={{ maxWidth: "100%" }}>
                <InlineDatePicker
                  onlyCalendar
                  keyboard
                  label={t("statusIndicator:final")}
                  disableFuture
                  invalidDateMessage={t("common:validation.invalid-date-format")}
                  maxDateMessage={t("common:validation.max-date")}
                  minDateMessage={t("common:validation.min-date")}
                  format="DD/MM/YYYY"
                  mask={(value) =>
                    value
                      ? [
                          /\d/,
                          /\d/,
                          "/",
                          /\d/,
                          /\d/,
                          "/",
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/,
                        ]
                      : []
                  }
                  disableOpenOnEnter
                  animateYearScrolling={false}
                  value={innerPeriodoColeta.final}
                  onChange={(e) => handlePeriodoColeta(e, "final")}
                />
                {erros.final !== "" && (
                  <FormHelperText error id="final-erro">
                    {erros.final}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {renderBotoes()}
          </Grid>
        </Grid>
      </ListItem>
      <Divider key="DividerFiltroPeriodoListItem" variant="middle" />
    </div>
  );
};
const mapDispatchToProps = {
  resetPeriodoColeta: farolActions.resetPeriodoColeta,
  alteraPeriodoColeta: farolActions.alteraPeriodoColeta,
  listaFarol: farolActions.listaFarol,
};

export default connect(null, mapDispatchToProps)(FiltroPeriodoColeta);
